import * as React from "react"

import { cn } from "@/lib/utils"
import type { Location } from "@/types/content"

/**
 * Country flags as emoji derived from the ISO 3166-1 alpha-2 code, so adding a
 * new datacenter in Strapi never requires shipping a new asset. Falls back to a
 * neutral globe when the CMS entry has no country code yet.
 */
export function FlagEmoji({
  countryCode,
  className,
}: {
  countryCode?: string | null
  className?: string
}) {
  const code = (countryCode ?? "").trim().toUpperCase()
  const emoji =
    code.length === 2
      ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
      : "🌐"

  return (
    <span className={cn("select-none leading-none", className)} role="img" aria-hidden>
      {emoji}
    </span>
  )
}

/** Compact "flag + name" pill used on plan cards. */
export function LocationPill({
  location,
  className,
  showLatency = false,
}: {
  location: Location
  className?: string
  showLatency?: boolean
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
        "bg-[var(--canvas-subtle)] text-[0.6875rem] font-semibold text-[var(--content-muted)]",
        "ring-1 ring-[var(--hairline)]",
        className,
      )}
    >
      <FlagEmoji countryCode={location.countryCode} className="text-[0.875rem]" />
      {location.name}
      {showLatency ? (
        <span className="text-[var(--content-subtle)]">· نامعلوم</span>
      ) : null}
    </span>
  )
}
